import { ILanguageResource } from "../types"
import { IPoeTranslation } from "./types"

function trim(str: string, char?: string) {
  if (typeof str !== "string") {
    throw new TypeError("First argument must be a string")
  }

  if (char === undefined) {
    return str.replace(/^\s+|\s+$/g, "")
  }

  const escapedChar = char.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")
  const regex = new RegExp("^" + escapedChar + "+|" + escapedChar + "+$", "g")
  return str.replace(regex, "")
}

function assignTranslationToResource(
  resource: ILanguageResource,
  { key, context, content }: IPoeTranslation
) {
  // skip assignment if not yet translated, except dynamic values
  if (
    content ||
    key === "notificationHeader" ||
    key === "collageGeneratingError"
  ) {
    let ctx = resource.translation
    context.split(".").forEach((rawContextKey) => {
      const contextKey = trim(rawContextKey, '"')
      if (!ctx[contextKey] || typeof ctx[contextKey] !== "object") {
        ctx[contextKey] = {}
      }

      ctx = ctx[contextKey] as ILanguageResource["translation"]
    })

    ctx[key] = content
  }
}

function flattenResourceTranslation(
  resTranslation: ILanguageResource["translation"],
  parents: string[] = []
): Record<string, string> {
  return Object.keys(resTranslation).reduce((acc, key) => {
    const keys = [...parents, key]

    if (typeof resTranslation[key] === "string") {
      return {
        ...acc,
        [keys.join(".")]: resTranslation[key] as string,
      }
    } else {
      return {
        ...acc,
        ...flattenResourceTranslation(
          resTranslation[key] as ILanguageResource["translation"],
          keys
        ),
      }
    }
  }, {} as Record<string, string>)
}

export function mapTranslationsToResource(
  translations: IPoeTranslation[],
  defaultResource?: ILanguageResource
): ILanguageResource {
  const resource: ILanguageResource = defaultResource ?? { translation: {} }
  translations.forEach((translation) =>
    assignTranslationToResource(resource, translation)
  )
  return resource
}

export function mapResourceToTranslations(
  resource: ILanguageResource
): IPoeTranslation[] {
  const flattenTranslations = flattenResourceTranslation(resource.translation)
  return Object.keys(flattenTranslations).map((pathKey) => {
    const contextKeys = pathKey.split(".")
    const key = contextKeys.pop()!

    return {
      key,
      context: contextKeys.map((contextKey) => `"${contextKey}"`).join("."),
      content: flattenTranslations[pathKey],
    }
  })
}
